import { AlertTriangle, Loader2, Package, Plus } from 'lucide-react';import type { InventoryLowStockItem } from '../../hooks/useInventorySnippetData';

interface InventoryLowStockPanelProps {
  items: InventoryLowStockItem[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onQuickOrder?: () => void;
  /** Sidebar column beside reports (control centre desktop). */
  layout?: 'full' | 'sidebar';
}
/** Low-stock alerts for the control-centre inventory widget. */
export default function InventoryLowStockPanel({
  items,
  loading = false,
  error = null,
  onRetry,
  onQuickOrder,
  layout = 'full',
}: InventoryLowStockPanelProps) {
  const panelFont =
    "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', system-ui, sans-serif";
  const isSidebar = layout === 'sidebar';

  const scrollAreaClass = isSidebar
    ? 'flex-1 min-h-0 overflow-y-auto ios-momentum-scroll p-2 pt-1.5 sm:p-2.5 sm:pt-2 max-lg:max-h-[12.5rem] max-lg:flex-none lg:max-h-[calc(min(380px,48vh)-2.75rem)]'
    : 'flex-1 min-h-0 overflow-y-auto ios-momentum-scroll p-2 pt-1.5 sm:p-2.5 sm:pt-2';

  return (
    <aside
      className={`relative z-0 flex flex-col overflow-hidden ${
        isSidebar
          ? 'h-full min-h-0'
          : 'h-[min(280px,38vh)] sm:h-[min(340px,44vh)] lg:h-[min(380px,48vh)]'
      }`}
      data-cc-inset
      data-inventory-low-stock
      aria-label="Low stock alerts"
      style={{ fontFamily: panelFont }}
    >
      <header
        className="flex shrink-0 items-center gap-2 border-b border-black/[0.06] bg-white px-3 py-2 sm:px-3 sm:py-2.5"
        data-cc-inset-header
      >
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" strokeWidth={2.25} />
        <h3
          className="text-[14px] font-semibold text-gray-900 sm:text-[14px]"
          style={{ letterSpacing: '-0.01em' }}
        >
          Low stock
        </h3>
        {!loading ? (
          <span
            className={`rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums leading-none ${
              items.length > 0 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-500'
            }`}
          >
            {items.length}
          </span>
        ) : null}
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" /> : null}
          {onQuickOrder ? (
            <button
              type="button"
              onClick={onQuickOrder}
              className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm transition-all hover:bg-blue-700 active:scale-[0.96] touch-manipulation sm:h-8 sm:w-8"
              aria-label="Quick order"
              title="Quick order"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <Plus className="h-4 w-4 sm:h-[18px] sm:w-[18px]" strokeWidth={2.5} />
            </button>
          ) : null}
        </div>
      </header>
      {loading ? (
        <div className="flex flex-1 items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : error ? (
        <div className="flex flex-1 flex-col items-center justify-center px-3 py-4 text-center">
          <p className="text-xs text-red-600 sm:text-sm">{error}</p>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="mt-2 text-[11px] font-semibold text-blue-600 sm:text-xs"
            >
              Retry
            </button>
          ) : null}
        </div>
      ) : items.length === 0 ? (
        <div
          className={`flex flex-1 flex-col items-center justify-center px-3 text-center sm:px-3 ${
            isSidebar ? 'py-5' : 'py-6'
          }`}
        >
          <Package
            className={`text-gray-300 ${isSidebar ? 'mb-2 h-8 w-8' : 'mb-2 h-9 w-9'}`}
            strokeWidth={1.5}
          />
          <p className={`font-medium text-gray-700 ${isSidebar ? 'text-[13px]' : 'text-[14px] sm:text-[15px]'}`}>
            Stock levels look good
          </p>
          <p className={`mt-0.5 text-gray-500 ${isSidebar ? 'text-[11px]' : 'text-[12px] sm:text-[13px]'}`}>
            No items at or below reorder point
          </p>
        </div>
      ) : (
        <div className={scrollAreaClass}>
          <ul className={`space-y-1.5 ${isSidebar ? '' : 'sm:space-y-1.5'}`}>
            {items.map((item) => {
              const isOut = item.quantity <= 0;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => onQuickOrder?.()}
                    className={`flex w-full items-center gap-2 rounded-xl border text-left transition-all touch-manipulation active:scale-[0.99] ${
                      isSidebar ? 'px-2.5 py-2' : 'px-2.5 py-2 sm:px-3 sm:py-2.5'
                    } ${
                      isOut
                        ? 'border-red-200/80 bg-red-50/50 hover:bg-red-50 active:bg-red-100/80'
                        : 'border-amber-200/80 bg-amber-50/40 hover:bg-amber-50 active:bg-amber-100/60'
                    }`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className="min-w-0 flex-1">
                      <p
                        className={`truncate font-semibold text-gray-900 ${
                          isSidebar ? 'text-[13px]' : 'text-[13px] sm:text-[14px]'
                        }`}
                        style={{ letterSpacing: '-0.005em' }}
                      >
                        {item.name}
                      </p>
                      <p
                        className={`truncate text-gray-500 mt-0.5 ${
                          isSidebar ? 'text-[11px]' : 'text-[11px] sm:text-[12px]'
                        }`}
                      >
                        {item.categoryName ? `${item.categoryName} · ` : ''}
                        Reorder at {item.reorderPoint} {item.unit}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p
                        className={`font-bold tabular-nums ${
                          isSidebar ? 'text-[13px]' : 'text-[13px] sm:text-[14px]'
                        } ${isOut ? 'text-red-700' : 'text-amber-800'}`}
                        style={{ letterSpacing: '-0.01em' }}
                      >
                        {item.quantity} {item.unit}
                      </p>
                      {isOut ? (
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-red-600 mt-0.5">
                          Out
                        </p>
                      ) : null}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </aside>
  );
}
